import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, InputObservable, trackByValue } from '@y42/angular/util';
import { map, Observable } from 'rxjs';
import { KeyboardShortcutsService } from './keyboard-shortcuts.service';

export type KeyboardShortcut = 'CmdEnter' | 'CmdK';

@UntilDestroy()
@Component({
  selector: 'y42-keyboard-shortcut',
  standalone: true,
  imports: [CommonModule],
  template: `
    <ng-container *ngFor="let key of state.shortcut; trackBy: trackByValue">
      <kbd [class]="shortcutClass">{{ key }}</kbd>
    </ng-container>
  `,
  styles: [
    `
      :host {
        @apply ml-space-x2 gap-space-x05;
        display: flex;
      }

      kbd {
        @apply p-space rounded-micro;
        display: inline-flex;
        min-width: 16px;
        height: 16px;
        justify-content: center;
        align-items: center;
        background: rgba(0, 0, 0, 0.3);
        text-transform: uppercase;
      }
    `,
  ],
})
export class KeyboardShortcutComponent {
  constructor(private readonly keyboardShortcutsService: KeyboardShortcutsService) {}

  @Input()
  @InputObservable()
  shortcut: KeyboardShortcut | string[] = 'CmdEnter';
  private readonly shortcut$!: Observable<KeyboardShortcut | string[]>;

  @Input() shortcutClass = '';

  private readonly keyboardShortcutToSymbols: Record<KeyboardShortcut, string[]> = {
    CmdEnter: this.getShortcutLabels('command+enter'),
    CmdK: this.getShortcutLabels('command+k'),
  };

  readonly trackByValue = trackByValue;

  readonly state = connectState(this, {
    shortcut: this.shortcut$.pipe(
      map((shortcut) => {
        if (typeof shortcut === 'string') {
          return this.keyboardShortcutToSymbols[shortcut];
        }

        return shortcut;
      }),
    ),
  });

  private getShortcutLabels(sequence: string) {
    return this.keyboardShortcutsService.getLabels(sequence).map((key) => key.decorator ?? key.base);
  }
}
